import { z } from 'zod';

/**
 * Money is an integer count of minor units (cents) plus a currency.
 *
 * Floating point is never used for money anywhere in this codebase: a budget
 * that is out by a cent is a support call, and `0.1 + 0.2` is not `0.3`.
 * Rounding happens once, explicitly, at the point of multiplication or parsing.
 */
export const CurrencySchema = z.string().length(3).toUpperCase();

export const MoneySchema = z.object({
  amount: z.number().int(),
  currency: CurrencySchema,
});

export type Money = { readonly amount: number; readonly currency: string };

export const DEFAULT_CURRENCY = 'USD';

export function money(amount: number, currency: string = DEFAULT_CURRENCY): Money {
  if (!Number.isFinite(amount)) {
    throw new TypeError(`Money amount must be finite, received ${amount}`);
  }
  if (!Number.isInteger(amount)) {
    throw new TypeError(
      `Money amount must be whole minor units (cents), received ${amount}. ` +
        'Round explicitly with multiplyMoney or parseMoneyInput instead.',
    );
  }
  return { amount, currency: currency.toUpperCase() };
}

export function zeroMoney(currency: string = DEFAULT_CURRENCY): Money {
  return money(0, currency);
}

export function isZeroMoney(value: Money): boolean {
  return value.amount === 0;
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new TypeError(
      `Cannot combine ${a.currency} with ${b.currency}. A company has one currency; ` +
        'converting between them is a product decision, not an arithmetic one.',
    );
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amount + b.amount, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amount - b.amount, a.currency);
}

export function negateMoney(value: Money): Money {
  return money(-value.amount, value.currency);
}

export function sumMoney(values: readonly Money[], currency: string = DEFAULT_CURRENCY): Money {
  return values.reduce<Money>((total, value) => addMoney(total, value), zeroMoney(currency));
}

/** Round half away from zero, which is how a person checking the line by hand rounds. */
function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

export function multiplyMoney(value: Money, quantity: number): Money {
  if (!Number.isFinite(quantity)) {
    throw new TypeError(`Quantity must be finite, received ${quantity}`);
  }
  return money(roundHalfAwayFromZero(value.amount * quantity), value.currency);
}

/**
 * Split an amount across weights so the parts add back to exactly the total.
 *
 * Used wherever one figure is apportioned across cost codes or draws. Naively
 * multiplying each share and rounding loses or gains cents; the remainder here
 * is handed out one unit at a time, largest fractional part first.
 */
export function allocateMoney(value: Money, weights: readonly number[]): Money[] {
  if (weights.length === 0) return [];
  if (weights.some((w) => !Number.isFinite(w) || w < 0)) {
    throw new TypeError('Allocation weights must be finite and non-negative');
  }
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight <= 0) {
    throw new TypeError('Allocation weights must sum to more than zero');
  }

  const exact = weights.map((w) => (value.amount * w) / totalWeight);
  const floored = exact.map((n) => Math.floor(n));
  let remainder = value.amount - floored.reduce((sum, n) => sum + n, 0);

  const order = exact
    .map((n, index) => ({ index, fraction: n - Math.floor(n) }))
    .sort((a, b) => b.fraction - a.fraction);

  const result = [...floored];
  for (const { index } of order) {
    if (remainder === 0) break;
    const step = remainder > 0 ? 1 : -1;
    result[index] = (result[index] ?? 0) + step;
    remainder -= step;
  }

  return result.map((amount) => money(amount, value.currency));
}

export function compareMoney(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.amount - b.amount;
}

export function formatMoney(value: Money, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: value.currency,
  }).format(value.amount / 100);
}

/**
 * Parse what someone types into a money field: currency symbols, thousands
 * separators and stray whitespace are all expected. Returns null rather than
 * throwing so a form can show a field error.
 *
 * The digits are read as a string rather than multiplied by 100, because
 * `1.005 * 100` is `100.49999999999999` in IEEE 754 and would round down to
 * $1.00. Anyone checking that line by hand expects $1.01.
 */
export function parseMoneyInput(input: string, currency: string = DEFAULT_CURRENCY): Money | null {
  const cleaned = input.replace(/[\s,$]/g, '');
  const match = /^(-)?(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (!match) return null;

  const [, sign, whole = '', fraction = ''] = match;
  if (whole === '' && fraction === '') return null;

  const wholeCents = (whole === '' ? 0 : Number(whole)) * 100;
  const centDigits = fraction.padEnd(3, '0');
  const cents = Number(centDigits.slice(0, 2));
  const roundUp = Number(centDigits[2]) >= 5;

  const magnitude = wholeCents + cents + (roundUp ? 1 : 0);
  if (!Number.isSafeInteger(magnitude)) return null;

  return money(sign === '-' ? -magnitude : magnitude, currency);
}
